import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing';
import type { Product, ProductType } from '@/domain/Product';
import type { Subscription } from '@/domain/Subscription';
import { generateEcommerceObject } from '@/utils/generateEcommerceObject';

export const RAISED_BY = 'global_plp_v3';

type BooleanString = 'true' | 'false';

type OptionalBooleanString = 'undefined' | BooleanString;

// the rule here is that data in the ECommerce section below does not
export type PlansItem = (
  | GtmProductPayload
  | GtmProductSubscription
  | GtmProductSubscriptionExtras
) & {
  item_added_by_user?: string | undefined;
};

export type Ecommerce = {
  items?: PlansItem[];
  currency?: string;
  value?: number;
  promotion_id?: string;
  promotion_name?: string;
  creative_slot?: string;
  creative_name?: string;
};

export type StructuredGtmPayload = {
  event: string;
  action_type: string;
  event_raised_by: string;
  cta_name?: string;
  ecommerce?: Ecommerce;
};

export type GtmPayload =
  | { [K in string]: string | Ecommerce | boolean | undefined }
  | StructuredGtmPayload;

export type GtmProductPayload = {
  item_id: string; // $product id, nessoft SKU internationalID
  item_name: string; // $product name, nessoft internationalname
  item_category: ProductType; // $product category, nessoft type: capsule|accessory|machine
  item_market_id: string; // $product market id, nessoft legacyId
  item_market_name: string; // $product market name, nessoft name
  item_range: string; // $product range, nessoft category
  item_technology: string; // $product technology, nessoft technology original|vertuo
  item_type: 'bundle' | 'single'; // $product type, nessoft bundled single|bundle
  item_discovery_offer: BooleanString;
  item_ecotax_applicable: BooleanString;
  item_added_by_user?: string;
  price?: number;
  discount?: number;
};

export type GtmProductPayloadEvent = {
  item_id_event: string; // $product id, nessoft SKU internationalID
  item_name_event: string; // $product name, nessoft internationalname
  item_category_event: ProductType; // $product category, nessoft type: capsule|accessory|machine
  item_price_event: number; // $price, price per capsule, machine, accessory
  item_market_id_event: string; // $product market id, nessoft legacyId
  item_market_name_event: string; // $product market name, nessoft name
  item_range_event: string; // $product range, nessoft category
  item_technology_event: string; // $product technology, nessoft technology original|vertuo
  item_type_event: 'bundle' | 'single'; // $product type, nessoft bundled single|bundle
};

export type GtmProductSubscription = {
  discount?: number;
  item_subscription_name: string; //  $name of the subscription, fill this with the "profile id" field in the subscription information
  item_subscription_category: string; //  $type of the subscription, fill this with the "type" field in the subscription information, should be machine
  item_subscription_price: number; //  $promotional price, fill this with the "promotionalPrice" field in the subscription information
  item_subscription_duration: string; //  $month duration, fill this with the "monthsDuration" field in the subscription information
  item_subscription_fee: string; //  $periodicFee, fill this with the "periodicFee" field in the subscription information
};

// now these fields are all optional due to https://nestle.atlassian.net/browse/ITNP-161372?focusedCommentId=3523467
export type GtmProductSubscriptionEvent = {
  item_subscription_name_event?: string;
  item_subscription_category_event?: string;
  item_subscription_price_event?: number;
  item_subscription_duration_event?: string;
  item_subscription_fee_event?: string;
};

export type GtmProductSubscriptionExtras = {
  item_brand: string;
  item_category: ProductType;
  item_category2: string;
  item_category3: string;
  item_category4: 'bundle' | 'single';
  quantity: number;
  affiliation: string;
  item_technology: string;
  item_range: string;

  item_market_id: string;
  item_market_name: string;
  item_type: 'bundle' | 'single';
  item_machine_shade?: string;
  item_in_stock?: 'in stock' | 'out of stock';

  item_avg_rating?: number;
  item_number_of_reviews?: number;

  item_machine_quantity_add?: number;
  item_capsule_quantity_add?: number;
  item_accessory_quantity_add?: number;

  item_list_name?: string;
  item_list_id?: string;
  index?: number;
};

export type GtmProductSubscriptionExtrasEvent = {
  item_brand_event: string;
  item_category_event: ProductType;
  item_category2_event: string;
  item_category3_event: string;
  item_category4_event: 'bundle' | 'single';
  item_quantity_event: number;
  affiliation: string;
  item_technology_event: string;
  item_range_event: string;

  item_market_id_event: string;
  item_market_name_event: string;
  item_type_event: 'bundle' | 'single';
  item_machine_shade_event?: string;
  item_in_stock_event?: 'in stock' | 'out of stock';

  item_avg_rating_event?: number;
  item_number_of_reviews_event?: number;

  item_machine_quantity_add_event?: number;
  item_capsule_quantity_add_event?: number;
  item_accessory_quantity_add_event?: number;

  item_list_name_event?: string;
  item_list_id_event?: string;
  index?: number;
};

export function dispatchNullEcommerceObject(): void {
  window?.gtmDataObject?.push({ ecommerce: null });
}

export default function dispatchTrackingEvent(payload: GtmPayload): void {
  window?.gtmDataObject?.push(payload);
}

export function productToGtmProductPayload(
  product: Product,
  isDiscoveryOffer: boolean,
  addedByUser?: boolean
): GtmProductPayload {
  const output = {
    item_id: product?.nessoft?.internationalId,
    item_name: product.name,
    item_category: product.type,
    item_market_id: product?.nessoft?.legacyId,
    item_market_name: product.name,
    item_range: product?.nessoft?.category,
    item_technology: product.technologies?.[0],
    item_type: product.bundle ? 'bundle' : 'single',
    item_discovery_offer: isDiscoveryOffer ? 'true' : 'false',
    item_ecotax_applicable: product.displayEcoTax ? 'true' : 'false',
    item_added_by_user:
      addedByUser !== undefined ? String(addedByUser) : undefined,
    discount: product.pricing.strikethroughPrice
      ? (product.pricing.strikethroughPrice - product.pricing.price) /
        PRICE_REPRESENTATION_MULTIPLIER
      : undefined // as per https://nestle.atlassian.net/browse/ITNP-161372?focusedCommentId=3523467
  } satisfies GtmProductPayload;

  return output;
}

export function productToGtmItemListPayload(
  product: Product,
  isDiscoveryOffer: boolean,
  trackingPosition: number,
  itemListId: string,
  itemListName: string
) {
  const output = {
    item_list_id: itemListId,
    item_list_name: itemListName,
    index: trackingPosition,
    item_brand: 'nespresso',
    item_category2: product.technologies[0],
    item_category3: product.nessoft?.category,
    item_category4: product.bundle ? 'bundle' : 'single',
    price: (product.pricing?.price ?? 0) / PRICE_REPRESENTATION_MULTIPLIER,
    quantity: product.bundle ? 1 : product.unitQuantity,
    affiliation: 'nespresso online store',
    item_in_stock: product.inStock ? 'in stock' : 'out of stock',
    ...productToGtmProductPayload(product, isDiscoveryOffer, false),
    ...generateEcommerceObject(product, isDiscoveryOffer, false)
  };

  return output;
}

export function productToGtmProductPayloadEvent(
  product: Product
): GtmProductPayloadEvent {
  return {
    item_id_event: product?.nessoft?.internationalId,
    item_name_event: product.name,
    item_category_event: product.type,
    item_price_event: product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER,
    item_market_id_event: product?.nessoft?.legacyId,
    item_market_name_event: product.name,
    item_range_event: product?.nessoft?.category,
    item_technology_event: product.technologies?.[0],
    item_type_event: product.bundle ? 'bundle' : 'single'
  };
}

export function subscriptionToGtmSubscriptionPayload(
  subscription: Subscription,
  product?: Product
): GtmProductSubscription {
  const { monthsDuration, promotionalPrice, periodicFee } =
    subscription.parameters;

  return {
    discount: product?.pricing.price
      ? (product.pricing.price - promotionalPrice) /
        PRICE_REPRESENTATION_MULTIPLIER
      : undefined, // as per https://nestle.atlassian.net/browse/ITNP-161372?focusedCommentId=3523467
    item_subscription_name: subscription.id,
    item_subscription_category: subscription.type?.toLocaleLowerCase(),
    item_subscription_price: promotionalPrice / PRICE_REPRESENTATION_MULTIPLIER,
    item_subscription_duration:
      monthsDuration === Infinity ? 'unlimited' : String(monthsDuration),
    item_subscription_fee: String(periodicFee / PRICE_REPRESENTATION_MULTIPLIER)
  };
}

export function subscriptionToGtmSubscriptionPayloadEvent(
  subscription?: Subscription,
  fieldsContain_event: boolean = true
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
) {
  if (subscription === undefined) {
    if (fieldsContain_event === false) {
      return {
        item_subscription_name: undefined,
        item_subscription_category: undefined,
        item_subscription_price: undefined,
        item_subscription_duration: undefined,
        item_subscription_fee: undefined
      };
    }

    return {
      item_subscription_name_event: undefined,
      item_subscription_category_event: undefined,
      item_subscription_price_event: undefined,
      item_subscription_duration_event: undefined,
      item_subscription_fee_event: undefined
    };
  }

  const { monthsDuration, promotionalPrice, periodicFee } =
    subscription.parameters;

  if (fieldsContain_event === false) {
    return {
      item_subscription_name: subscription.id,
      item_subscription_category: subscription.type?.toLocaleLowerCase(),
      item_subscription_price:
        promotionalPrice / PRICE_REPRESENTATION_MULTIPLIER,
      item_subscription_duration:
        monthsDuration === Infinity ? 'unlimited' : String(monthsDuration),
      item_subscription_fee: String(
        periodicFee / PRICE_REPRESENTATION_MULTIPLIER
      )
    };
  }

  return {
    item_subscription_name_event: subscription.id,
    item_subscription_category_event: subscription.type?.toLocaleLowerCase(),
    item_subscription_price_event:
      promotionalPrice / PRICE_REPRESENTATION_MULTIPLIER,
    item_subscription_duration_event:
      monthsDuration === Infinity ? 'unlimited' : String(monthsDuration),
    item_subscription_fee_event: String(
      periodicFee / PRICE_REPRESENTATION_MULTIPLIER
    )
  };
}

/**
 * This generates some partial product information that it appears every item in the google analytics
 * needed for backwards compatibility
 * @param product Product
 * @param subscription Subscription
 * @param index number
 * @returns GtmProductSubscriptionExtras
 */
export function subscriptionToGtmSubscriptionExtrasPayload(
  product: Product,
  subscription: Subscription,
  index: number,
  duration?: number
): GtmProductSubscriptionExtras {
  return {
    item_brand: 'nespresso',
    item_category: product.type,
    item_category2: product.technologies?.[0],
    item_category3: product.range?.category.split('/')[2],
    item_category4: product.bundle ? 'bundle' : 'single',
    item_market_id: product.nessoft?.internationalId,
    item_market_name: product.nessoft?.internationalName,
    item_range: product.range?.category.split('/')[2],
    item_technology: product.technologies?.[0],
    item_type: product.bundle ? 'bundle' : 'single',
    affiliation: 'Nespresso Online Store',
    item_machine_shade: product.color?.value,
    item_in_stock: product.inStock ? 'in stock' : 'out of stock',
    quantity: 1,
    item_list_name:
      Number(duration) > 0
        ? `subscription - ${duration} months`
        : `subscription options`,
    item_list_id: subscription.id,
    index
  };
}

/**
 * This generates some partial product information that it appears every item in the google analytics
 * needed for backwards compatibility
 * @param product Product
 * @param subscription Subscription
 * @param index number
 * @returns GtmProductSubscriptionExtrasEvent
 */
export function subscriptionToGtmSubscriptionExtrasPayloadEvent(
  product: Product,
  subscription: Subscription,
  index: number,
  duration?: number
): GtmProductSubscriptionExtrasEvent {
  return {
    item_brand_event: 'nespresso',
    item_category_event: product.type,
    item_category2_event: product.technologies?.[0],
    item_category3_event: product.range?.category.split('/')[2],
    item_category4_event: product.bundle ? 'bundle' : 'single',
    item_market_id_event: product.nessoft?.internationalId,
    item_market_name_event: product.nessoft?.internationalName,
    item_range_event: product.range?.category.split('/')[2],
    item_technology_event: product.technologies?.[0],
    item_type_event: product.bundle ? 'bundle' : 'single',
    affiliation: 'Nespresso Online Store',
    item_machine_shade_event: product.color?.value,
    item_in_stock_event: product.inStock ? 'in stock' : 'out of stock',
    item_quantity_event: 1,
    item_list_name_event:
      Number(duration) > 0
        ? `subscription - ${duration} months`
        : `subscription options`,
    item_list_id_event: subscription.id,
    index
  };
}

/**
 * Tracking guides:
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#4.4.-PDP-quick-View
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#5.2.1.-CTA-Tracking-Inside-the-modal-of-the-hero-banner
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#6.4.-CTA-Tracking-Inside-the-modal-of-the-promo-card
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#8.4.1.--%E2%80%9CWhat-is-the-difference%E2%80%9D-link-click
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#8.4.2.-%E2%80%9CWhat-is-the-difference%E2%80%9D-modal
 */
export const trackPlpInteraction = ({
  actionType,
  clickLocation,
  ctaName,
  product
}: {
  actionType: string;
  clickLocation?: string;
  ctaName?: string;
  product?: Product;
}) => {
  dispatchTrackingEvent({
    event: 'plp_interaction',
    event_raised_by: RAISED_BY,
    action_type: actionType,
    ...(clickLocation && { click_location: clickLocation }),
    ...(ctaName && { cta_name: ctaName }),
    ...(product && productToGtmProductPayloadEvent(product))
  });
};

/**
 * Tracking plan:
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#6.2.-Click
 * https://nestle.atlassian.net/wiki/x/MQdWEQ#5.2.-Click
 */
export const trackSelectPromotion = ({
  promotionId,
  promotionName,
  creativeName,
  creativeSlot,
  ctaName
}: {
  promotionId: string;
  promotionName: string;
  creativeName: string;
  creativeSlot?: string;
  ctaName: string;
}) => {
  dispatchTrackingEvent({
    event: 'select_promotion',
    event_raised_by: RAISED_BY,
    cta_name: ctaName,
    ecommerce: {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_slot: creativeSlot,
      creative_name: creativeName
    }
  });
};

export function trackAdobeTarget(
  experiment: string,
  variant: string,
  actionName?: string
) {
  const event: Record<string, string> = {
    event: 'adobe_target',
    event_raised_by: 'adobe target',
    experiment_id: experiment,
    experiment_type: 'AB',
    experiment_name: experiment,
    experiment_variant_id: variant,
    experiment_variant: variant
  };

  if (actionName) {
    event.experiment_action = actionName;
  }
  dispatchTrackingEvent(event);
}

export function trackOptimizely(
  experiment: string,
  variant: string,
  actionName?: string
) {
  const event: Record<string, string> = {
    event: 'optimizely_decision_web',
    experiment_name: experiment,
    experiment_variant: variant,
    event_raised_by: 'Optimizely custom event'
  };

  if (actionName) {
    event.experiment_action = actionName;
  }
  dispatchTrackingEvent(event);
}

export function trackVariants(experimentsWithVariants: Set<string>) {
  const isOptimizely = Boolean(
    document.querySelector('script[src*="optimizely"]')
  );

  for (const experimentWithVariant of experimentsWithVariants) {
    const parts = (experimentWithVariant ?? '').split('.');
    const variantName = parts.pop() ?? experimentWithVariant;
    const experimentName = parts.join('.');
    const track = isOptimizely ? trackOptimizely : trackAdobeTarget;
    track(experimentName, variantName);
  }
}

export function trackVariantAction(
  experimentWithVariant: string,
  actionName: string
) {
  const isOptimizely = Boolean(
    document.querySelector('script[src*="optimizely"]')
  );

  const parts = (experimentWithVariant ?? '').split('.');
  const variantName = parts.pop() ?? experimentWithVariant;
  const experimentName = parts.join('.');
  const track = isOptimizely ? trackOptimizely : trackAdobeTarget;

  track(experimentName, variantName, actionName);
}
